import moment from 'moment';
import { PHASES } from '../config/app.config';

function hasText(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function isObjectIdString(value) {
  return typeof value === 'string' && /^[a-fA-F0-9]{24}$/.test(value);
}

function formatSubmitted(dateStr, timeStr, omitTime = false) {
  if (!hasText(dateStr)) return '';
  if (omitTime || !hasText(timeStr)) return dateStr;
  const t = moment(timeStr, ['HH:mm', 'hh:mm A'], true);
  const timeOut = t.isValid() ? t.format('hh:mm A') : timeStr;
  return `${dateStr} ${timeOut}`;
}

function formatDoctor(doctor) {
  if (!doctor) return '';
  if (typeof doctor === 'string') {
    if (isObjectIdString(doctor)) return '';
    return doctor;
  }
  const name = [doctor.firstName, doctor.lastName].filter(Boolean).join(' ');
  if (hasText(name)) return name.replace(/^Dr\.?\s*/i, '').trim();
  if (hasText(doctor.fullName)) return doctor.fullName;
  return '';
}

function padLabel(label) {
  return String(label).padEnd(3, ' ');
}

const BODY_MAX = 68; // column-2 width; hang = 3-char label + 1 space
const HANG = '    ';

function wrapBody(text, width = BODY_MAX) {
  const paragraphs = String(text).split('\n');
  const out = [];

  paragraphs.forEach((para) => {
    const words = para.length ? para.split(/\s+/) : [''];
    let line = '';

    words.forEach((word) => {
      if (!line) {
        let rest = word;
        while (rest.length > width) {
          out.push(rest.slice(0, width));
          rest = rest.slice(width);
        }
        line = rest;
        return;
      }
      if (line.length + 1 + word.length <= width) {
        line += ` ${word}`;
        return;
      }
      out.push(line);
      let rest = word;
      while (rest.length > width) {
        out.push(rest.slice(0, width));
        rest = rest.slice(width);
      }
      line = rest;
    });

    out.push(line);
  });

  return out;
}

function formatLabeledLine(label, body) {
  const parts = wrapBody(body);
  if (!parts.length) return padLabel(label);
  const first = `${padLabel(label)} ${parts[0]}`;
  const rest = parts.slice(1).map((p) => `${HANG}${p}`);
  return [first, ...rest].join('\n');
}

/** Label + kind on one line; URL below, wrapped at BODY_MAX, no indent. */
function formatAttachmentLine(label, kind, url) {
  const width = BODY_MAX;
  const s = String(url);
  const parts = [];
  let rest = s;
  while (rest.length > width) {
    parts.push(rest.slice(0, width));
    rest = rest.slice(width);
  }
  parts.push(rest);
  return [`${label} ${kind}:`, ...parts].join('\n');
}

function attachmentLabel(msgNum, index, total) {
  if (total === 1) return String(msgNum);
  return `${msgNum}${String.fromCharCode(97 + index)}`; // 5a, 5b, …
}

function collectGalleryAttachments(message) {
  const images = Array.isArray(message.gallery) ? message.gallery : [];
  return images
    .filter((url) => hasText(url))
    .map((url) => ({ kind: 'Image', url: String(url).trim() }));
}

/**
 * Clipboard transcript for Status Update Archive (facility updates only).
 * Clipboard order is chronological (opposite of archive UI).
 * Left column is 3 chars (flush left; covers labels like 25a), then body.
 */
export function buildStatusUpdateArchiveTranscript({
  appointmentType = '',
  header = {},
  messages = [],
}) {
  const lines = ['Loyal Message History'];

  if (hasText(appointmentType) && !isObjectIdString(appointmentType)) {
  lines.push(appointmentType);
}

  lines.push(`Parent: ${header.parent || ''}`);
  lines.push(`Pet: ${header.pet || ''}`);

  const doctorLabel = hasText(header.doctor) ? String(header.doctor).trim() : '';
  const doctorName = doctorLabel.replace(/^Dr\.?\s*/i, '').trim();
  if (
    doctorName &&
    doctorName.toUpperCase() !== 'N/A' &&
    !isObjectIdString(doctorName)
  ) {
    lines.push(`Doctor: ${doctorName}`);
  }

  lines.push(`Submitted: ${header.submitted || ''}`);
  lines.push(`Copied: ${moment().format('MM/DD/YYYY hh:mm A')}`);

  // UI is newest-first; paste oldest → newest
  const chrono = [...(messages || [])].reverse();

  const messageLines = chrono.map((m, i) => {
    const msgNum = i + 1;
    const dt = m.dateTime ? moment(m.dateTime) : null;
    const date = dt && dt.isValid() ? dt.format('MM/DD/YYYY') : '';
    const time = dt && dt.isValid() ? dt.format('hh:mm A') : '';
    const phase =
      m.phaseId != null && PHASES[m.phaseId] ? PHASES[m.phaseId] : '';
    const body = m.message || '';
    const prefix = [date, time].filter(Boolean).join(' ');
    const withPhase = phase
      ? `${prefix} [${phase}] ${body}`
      : `${prefix} ${body}`;
    const main = withPhase.trim();
    const hasAtt = collectGalleryAttachments(m).length > 0;
    const withMark = hasAtt ? `${main} [+]` : main;
    return formatLabeledLine(msgNum, withMark);
  });

  const attachmentLines = [];
  chrono.forEach((m, i) => {
    const msgNum = i + 1;
    const atts = collectGalleryAttachments(m);
    atts.forEach((att, attIdx) => {
      const label = attachmentLabel(msgNum, attIdx, atts.length);
      attachmentLines.push(
        formatAttachmentLine(label, att.kind, att.url),
      );
    });
  });

  const parts = [
  lines.join('\n'),
  '',
  '[+] = message has attachment(s)',
  '',
  'MESSAGE HISTORY:',
  messageLines.join('\n\n'),
];

if (attachmentLines.length) {
  parts.push('', 'MESSAGE ATTACHMENTS:', attachmentLines.join('\n\n'));
}

  return parts.join('\n').trimEnd();
}

export function buildStatusUpdateArchiveHeader(data = {}) {
  const petSummary = data.petSummary || {};
  return {
    parent: petSummary.petParentName || '',
    pet: petSummary.name || '',
    doctor: formatDoctor(data.doctor),
    submitted: formatSubmitted(
      data.dateTime,
      data.time,
      !!data.createdFromTrackingBoard,
    ),
  };
}
